import { calculatePayoutBaseUnits } from '@/utils/payout';
import { extractTotalGasUsed } from '@/utils/gas';
import type { AutoBetExecutorResult } from './useAutoBetRunner';
import type { GamePlayAbortReason, GamePlayHelpers, GameTransactionRunner } from './useGameTransactionController';

type GetPayoutInput = {
	stakeBaseUnits: number;
	rewardBaseUnits: number;
	didWin: boolean;
};

export type StandardRoundResult<TParsed> = AutoBetExecutorResult & {
	parsed?: TParsed;
};

export type StandardRoundSuccessArgs<TParsed> = {
	parsed: TParsed;
	rewardBaseUnits: number;
	stakeBaseUnits: number;
	didWin: boolean;
	helpers: GamePlayHelpers;
};

export type StandardRoundOptions<TParsed> = {
	betAmount: number;
	runGamePlay: GameTransactionRunner;
	toBaseUnits: (amount: number) => number;
	currencyType?: string;
	validators?: Array<() => boolean>;
	createTransaction: (stakeBaseUnits: number) => any;
	parseResponse: (response: any) => TParsed;
	getBetId: (parsed: TParsed) => string | null | undefined;
	getRewardBaseUnits: (parsed: TParsed) => number;
	getDidWin?: (parsed: TParsed) => boolean;
	getPayoutBaseUnits?: (input: GetPayoutInput) => number;
	getGasBaseUnits?: (response: any) => number;
	onBalanceReserved?: (helpers: GamePlayHelpers) => void;
	onAfterSuccess?: (args: StandardRoundSuccessArgs<TParsed>) => Promise<void> | void;
	onError?: (error: unknown, helpers: GamePlayHelpers) => void;
};

/**
 * Shared runner for single-round games. Handles transaction plumbing, pending balance bookkeeping,
 * gas/payout computation, and bet registration, while letting callers supply game-specific bits.
 */
export async function runStandardGameRound<TParsed>({
	betAmount,
	runGamePlay,
	toBaseUnits,
	currencyType,
	validators = [],
	createTransaction,
	parseResponse,
	getBetId,
	getRewardBaseUnits,
	getDidWin = (parsed) => getRewardBaseUnits(parsed) > 0,
	getPayoutBaseUnits = ({ stakeBaseUnits, rewardBaseUnits, didWin }) =>
		calculatePayoutBaseUnits({ _stakeBaseUnits: stakeBaseUnits, rawRewardBaseUnits: rewardBaseUnits, didWin }),
	getGasBaseUnits = extractTotalGasUsed,
	onBalanceReserved,
	onAfterSuccess,
	onError,
}: StandardRoundOptions<TParsed>): Promise<StandardRoundResult<TParsed>> {
	return new Promise<StandardRoundResult<TParsed>>((resolve, reject) => {
		let settled = false;
		let stakeSnapshot = 0;

		const settleOnce = (next: () => void) => {
			if (settled) return;
			settled = true;
			next();
		};

		const resolveAborted = (reason: GamePlayAbortReason) => {
			settleOnce(() =>
				resolve({
					status: 'aborted',
					abortReason: reason,
					win: false,
					stakeBaseUnits: 0,
					rewardBaseUnits: 0,
					profitBaseUnits: 0,
				}),
			);
		};

		runGamePlay({
			betAmount,
			toBaseUnits,
			currencyType,
			validators,
			createTransaction: (stakeBaseUnits) => {
				stakeSnapshot = stakeBaseUnits;
				return createTransaction(stakeBaseUnits);
			},
			onAbort: resolveAborted,
			onBalanceReserved,
			onSuccess: async (response, helpers) => {
				try {
					const parsed = parseResponse(response);
					const rewardBaseUnits = getRewardBaseUnits(parsed);
					const didWin = getDidWin(parsed);
					const betId = getBetId(parsed);

					if (helpers.balanceBetId) {
						const gasBaseUnits = getGasBaseUnits(response);
						helpers.setGas?.({ balanceBetId: helpers.balanceBetId, gasBaseUnits });
						const payoutBaseUnits = getPayoutBaseUnits({ stakeBaseUnits: stakeSnapshot, rewardBaseUnits, didWin });
						helpers.setPayout({ balanceBetId: helpers.balanceBetId, payoutBaseUnits });
					}

					if (betId) {
						helpers.registerBetId({ betId, game: helpers.game });
					}

					await onAfterSuccess?.({
						parsed,
						rewardBaseUnits,
						stakeBaseUnits: stakeSnapshot,
						didWin,
						helpers,
					});

					settleOnce(() =>
						resolve({
							status: 'ok',
							parsed,
							win: didWin,
							stakeBaseUnits: stakeSnapshot,
							rewardBaseUnits,
							profitBaseUnits: rewardBaseUnits - stakeSnapshot,
						}),
					);
					return;
				} catch (error) {
					settleOnce(() => reject(error as Error));
				}
			},
			onError: (error, helpers) => {
				onError?.(error, helpers);
				settleOnce(() => reject(error));
			},
		});
	});
}
